import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { AmbientBg } from "@/components/studio/atoms";
import { ChatPromptDock } from "@/components/studio/ChatPromptDock";
import { SubjectLockPill } from "@/components/studio/SubjectLockPill";
import { useSubjectLock } from "@/hooks/useSubjectLock";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";
import {
  Loader2, Plus, Save, Trash2,
  Wand2, Film, FolderOpen, Sparkles, LayoutGrid, Volume2, VolumeX,
} from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SceneNode, NodeConnector } from "@/components/storyboard/SceneNode";
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors,
} from "@dnd-kit/core";
import {
  SortableContext, sortableKeyboardCoordinates, rectSortingStrategy,
} from "@dnd-kit/sortable";

const PIPELINE_LS_KEY = "geneo_pipeline_history_v1";

const NEW_SCENE = (order) => ({
  order,
  title: "",
  description: "",
  character_generation_id: null,
  character_image_url: null,
  uploaded_image_url: null,
  omni_references: [],
  _uid: `s_${Math.random().toString(36).slice(2, 10)}`,
});

const withUid = (s, i) => ({
  title: "",
  omni_references: [],
  uploaded_image_url: null,
  ...s,
  order: i,
  _uid: s._uid || `s_${Math.random().toString(36).slice(2, 10)}`,
});

export default function Storyboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [boards, setBoards] = useState([]);
  const [loadingBoards, setLoadingBoards] = useState(true);
  const [activeId, setActiveId] = useState(null);
  const [title, setTitle] = useState("Untitled Storyboard");
  const [scenes, setScenes] = useState([NEW_SCENE(0)]);
  const [saving, setSaving] = useState(false);
  const [picking, setPicking] = useState(null);
  const [pickerTab, setPickerTab] = useState("gallery");
  const [gallery, setGallery] = useState([]);
  const [pipelineItems, setPipelineItems] = useState([]);
  const [loadingGallery, setLoadingGallery] = useState(false);
  const [reelBuilding, setReelBuilding] = useState(false);
  const [narrationOn, setNarrationOn] = useState(true);
  const [voice, setVoice] = useState("rachel");
  const [voices, setVoices] = useState([]);
  // ID of the scene currently under the drag pointer — drives the gold-glow
  // "drop target" ring. `null` when no drag is in progress. Set by
  // `handleDragOver` and cleared on drag-end/cancel.
  const [dragOverSceneId, setDragOverSceneId] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => {
    api.get("/tts/voices").then((r) => setVoices(r.data?.voices || [])).catch(() => {});
  }, []);

  const fetchBoards = async () => {
    setLoadingBoards(true);
    try {
      const { data } = await api.get("/storyboards");
      setBoards(data || []);
    } catch (e) {
      toast.error("Failed to load storyboards");
    } finally {
      setLoadingBoards(false);
    }
  };

  useEffect(() => {
    if (!user) return;
    fetchBoards();
  }, [user]);

  // Accept incoming "Send to Storyboard" from Studio pipeline recent tiles
  useEffect(() => {
    const incoming = location.state?.addScene;
    if (!incoming) return;
    setScenes((prev) => {
      const isEmptyDraft = prev.length === 1 && !prev[0].description && !prev[0].character_image_url && !prev[0].uploaded_image_url && !prev[0].title;
      const newScene = {
        ...NEW_SCENE(isEmptyDraft ? 0 : prev.length),
        // Character name (when sent from a Character Sheet) populates the scene title
        title: incoming.character_name || "",
        description: incoming.description || "",
        character_generation_id: incoming.character_generation_id || null,
        character_image_url: incoming.character_image_url || null,
        // Character Sheet reference views (all 10 angles) preload as omni references
        omni_references: Array.isArray(incoming.omni_references) ? incoming.omni_references : [],
        // Parallel tag array — each entry is the friendly view name for the
        // corresponding omni_reference at the same index (Feb 2026). Empty
        // string for uploads that don't carry a semantic label.
        omni_reference_tags: Array.isArray(incoming.omni_reference_tags) ? incoming.omni_reference_tags : [],
      };
      return isEmptyDraft ? [newScene] : [...prev, { ...newScene, order: prev.length }];
    });
    navigate(location.pathname, { replace: true, state: null });
  }, [location.state, location.pathname, navigate]);

  const openBoard = async (id) => {
    try {
      const { data } = await api.get(`/storyboards/${id}`);
      setActiveId(data.storyboard_id);
      setTitle(data.title || "Untitled");
      setScenes((data.scenes || []).map(withUid));
    } catch {
      toast.error("Failed to open storyboard");
    }
  };

  const startNew = () => {
    setActiveId(null);
    setTitle("Untitled Storyboard");
    setScenes([NEW_SCENE(0)]);
  };

  const addScene = () => setScenes((prev) => [...prev, NEW_SCENE(prev.length)]);
  const removeScene = (i) => setScenes((prev) => prev.filter((_, k) => k !== i).map((s, idx) => ({ ...s, order: idx })));
  const moveScene = (i, dir) => setScenes((prev) => {
    const j = i + dir;
    if (j < 0 || j >= prev.length) return prev;
    const arr = [...prev];
    [arr[i], arr[j]] = [arr[j], arr[i]];
    return arr.map((s, idx) => ({ ...s, order: idx }));
  });
  const updateScene = (i, patch) => setScenes((prev) => prev.map((s, idx) => idx === i ? { ...s, ...patch } : s));

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setDragOverSceneId(null);
    if (!over || active.id === over.id) return;
    // Swap semantics (matches the attachment rail pattern) — dragging scene A
    // onto scene B exchanges their positions instead of shifting every scene
    // in between. Users get a predictable "trade slots" gesture; a full
    // insert-shift is now Move-Up / Move-Down buttons.
    setScenes((prev) => {
      const from = prev.findIndex((s) => s._uid === active.id);
      const to = prev.findIndex((s) => s._uid === over.id);
      if (from < 0 || to < 0 || from === to) return prev;
      const next = prev.slice();
      [next[from], next[to]] = [next[to], next[from]];
      return next.map((s, idx) => ({ ...s, order: idx }));
    });
  };

  // Track the current over-target during a drag so we can light up the
  // hovered scene with a gold-glow ring. Also fires on scroll while dragging.
  const handleDragOver = (event) => {
    const overId = event?.over?.id ?? null;
    setDragOverSceneId(overId);
  };
  const handleDragCancel = () => setDragOverSceneId(null);

  const loadPipeline = () => {
    try {
      const raw = localStorage.getItem(PIPELINE_LS_KEY);
      const items = raw ? JSON.parse(raw) : [];
      // items look like {id, image_url, prompt, ...}
      setPipelineItems(Array.isArray(items) ? items.filter((it) => it.image_url) : []);
    } catch {
      setPipelineItems([]);
    }
  };

  const openGalleryPicker = async (sceneIdx) => {
    setPicking(sceneIdx);
    setPickerTab("gallery");
    loadPipeline();
    if (gallery.length === 0) {
      setLoadingGallery(true);
      try {
        const { data } = await api.get("/generations?scope=mine");
        setGallery((data || []).filter((g) => g.image_url && !g.video_url));
      } catch {
        toast.error("Failed to load gallery");
      } finally {
        setLoadingGallery(false);
      }
    }
  };

  const pickCharacter = (item) => {
    if (picking == null) return;
    updateScene(picking, {
      character_generation_id: item.generation_id || item.id || null,
      character_image_url: item.image_url,
    });
    setPicking(null);
    toast.success("Character pinned to scene.");
  };

  const clearCharacter = (i) => updateScene(i, { character_generation_id: null, character_image_url: null });
  const clearUploaded = (i) => updateScene(i, { uploaded_image_url: null });

  const uploadSceneImage = async (i, file, kind = "frame") => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Only image files allowed."); return; }
    if (file.size > 15 * 1024 * 1024) { toast.error("Image must be under 15MB."); return; }
    const toastId = toast.loading(kind === "frame" ? "Uploading scene image…" : "Adding reference…");
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("note", `Scene ${i + 1} ${kind}`);
      const { data } = await api.post("/upload/image", form, { headers: { "Content-Type": "multipart/form-data" } });
      if (kind === "frame") {
        updateScene(i, { uploaded_image_url: data.image_url });
        toast.success("Scene image uploaded.", { id: toastId });
      } else {
        setScenes((prev) => prev.map((s, idx) => {
          if (idx !== i) return s;
          const existing = s.omni_references || [];
          if (existing.length >= 4) return s;
          return { ...s, omni_references: [...existing, data.image_url] };
        }));
        toast.success("Reference added.", { id: toastId });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed", { id: toastId });
    }
  };

  const removeOmni = (i, oi) => setScenes((prev) => prev.map((s, idx) => {
    if (idx !== i) return s;
    const arr = [...(s.omni_references || [])];
    const tags = [...(s.omni_reference_tags || [])];
    arr.splice(oi, 1);
    tags.splice(oi, 1);
    return { ...s, omni_references: arr, omni_reference_tags: tags };
  }));

  // Drag-drop from the Reference Preview Rail — attach a dragged angle URL
  // onto another scene as either the primary frame or an additional omni ref.
  const dropRefOnScene = (destIndex, url, target, tag) => {
    if (!url) return;
    if (target === "frame") {
      setScenes((prev) => prev.map((s, idx) => {
        if (idx !== destIndex) return s;
        return { ...s, character_image_url: url, uploaded_image_url: null };
      }));
      toast.success(`${tag ? `${tag} angle` : "Angle"} dropped onto scene ${destIndex + 1} — set as primary frame.`);
      return;
    }
    // "omni" target — append to that scene's reference rail (dedup, cap 10)
    setScenes((prev) => prev.map((s, idx) => {
      if (idx !== destIndex) return s;
      const existing = s.omni_references || [];
      const existingTags = s.omni_reference_tags || [];
      if (existing.includes(url)) return s;
      if (existing.length >= 10) {
        toast.error(`Scene ${destIndex + 1}'s rail is full (10 max).`);
        return s;
      }
      return {
        ...s,
        omni_references: [...existing, url],
        omni_reference_tags: [...existingTags, tag || ""],
      };
    }));
    toast.success(`${tag ? `${tag} angle` : "Angle"} added to scene ${destIndex + 1}'s reference rail.`);
  };

  const saveBoard = async () => {
    setSaving(true);
    try {
      const payload = {
        title: title || "Untitled Storyboard",
        scenes: scenes.map((s, i) => ({
          order: i,
          title: (s.title || "").trim(),
          description: (s.description || "").trim(),
          character_generation_id: s.character_generation_id || null,
          character_image_url: s.character_image_url || null,
          uploaded_image_url: s.uploaded_image_url || null,
          omni_references: s.omni_references || [],
        })),
      };
      let data;
      if (activeId) {
        ({ data } = await api.put(`/storyboards/${activeId}`, payload));
      } else {
        ({ data } = await api.post("/storyboards", payload));
        setActiveId(data.storyboard_id);
      }
      toast.success(activeId ? "Storyboard updated." : "Storyboard saved.");
      fetchBoards();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const deleteBoard = async (id) => {
    if (!window.confirm("Delete this storyboard? This can't be undone.")) return;
    try {
      await api.delete(`/storyboards/${id}`);
      if (activeId === id) startNew();
      fetchBoards();
      toast.success("Deleted.");
    } catch {
      toast.error("Delete failed");
    }
  };

  const generateReel = async () => {
    if (!activeId) { toast.error("Save the storyboard first."); return; }
    if (scenes.length === 0 || scenes.every((s) => !s.character_image_url && !s.uploaded_image_url && !s.description)) {
      toast.error("Add at least one scene with an image or description.");
      return;
    }
    setReelBuilding(true);
    try {
      const qs = new URLSearchParams({
        background: "true",
        narrate: narrationOn ? "true" : "false",
        voice,
      }).toString();
      await api.post(`/storyboards/${activeId}/generate-reel?${qs}`);
      toast.success(narrationOn ? "Narrated reel queued — we'll ping you when it's ready." : "Silent reel queued — we'll ping you when it's ready.");
      setTimeout(() => fetchBoards(), 5000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reel build failed");
    } finally {
      setReelBuilding(false);
    }
  };

  const sceneCount = scenes.length;
  const boardHasReel = !!boards.find((b) => b.storyboard_id === activeId)?.reel_generation_id;

  return (
    <div className="min-h-screen bg-[#07070C] glyph-bg relative">
      <AmbientBg variant="storyboard" />
      <Navbar />

      <div className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        {/* Hero */}
        <div className="relative mb-8 rounded-3xl overflow-hidden border border-[#F2C94C]/25 bg-gradient-to-br from-[#1a0d24] via-[#0A0A0A] to-[#0d1f24] p-6 md:p-8 action-stripes">
          <div className="absolute -top-10 -right-10 w-56 h-56 rounded-full bg-[#F2C94C]/15 blur-3xl pointer-events-none" />
          <div className="absolute -bottom-16 -left-16 w-72 h-72 rounded-full bg-[#00E5FF]/10 blur-3xl pointer-events-none" />
          <div className="relative flex items-start justify-between flex-wrap gap-4">
            <div>
              <div className="inline-flex items-center gap-2 font-mono-hy text-[10px] text-[#F2C94C] mb-3 px-3 py-1 rounded-full bg-[#F2C94C]/10 border border-[#F2C94C]/30">
                <LayoutGrid className="w-3 h-3" /> STORYBOARD · NODE CANVAS v2
              </div>
              <h1 className="font-display font-bold text-4xl md:text-5xl tracking-tight">
                Sequence your <span className="bg-gradient-to-r from-[#F2C94C] via-[#E27D60] to-[#00E5FF] bg-clip-text text-transparent">story</span>.
              </h1>
              <span className="streak mt-3" />
              <p className="font-body text-[#D9D2BE] mt-2 max-w-xl text-sm md:text-base">
                Chain nodes into a shot list. Drag one scene onto another to swap slots, upload custom frames, pin references, and pull characters from your pipeline or gallery.
              </p>
            </div>
            <div className="flex flex-col items-end gap-2">
              <div className="font-mono-hy text-[10px] text-white">{sceneCount} NODE{sceneCount !== 1 ? "S" : ""} · IN PROGRESS</div>
              <button
                onClick={startNew}
                data-testid="storyboard-new-btn"
                className="font-mono-hy text-[10px] px-4 py-2 rounded-full border border-[#F2C94C]/30 text-[#F2C94C] hover:bg-[#F2C94C]/10 flex items-center gap-2"
              >
                <Plus className="w-3.5 h-3.5" /> New
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-6">
          {/* LEFT · Saved storyboards */}
          <aside className="col-span-12 lg:col-span-3 space-y-3">
            <div className="p-4 rounded-2xl bg-[#120E15] border border-[#F2C94C]/15">
              <div className="font-mono-hy text-[10px] text-[#F2C94C] mb-3 flex items-center gap-2">
                <FolderOpen className="w-3 h-3" /> // Saved storyboards
              </div>
              {loadingBoards ? (
                <div className="flex justify-center py-6"><Loader2 className="w-4 h-4 animate-spin text-[#F2C94C]" /></div>
              ) : boards.length === 0 ? (
                <div className="font-mono-hy text-[9px] text-white text-center py-4">No storyboards yet.</div>
              ) : (
                <div className="space-y-2" data-testid="storyboard-list">
                  {boards.map((b) => (
                    <div
                      key={b.storyboard_id}
                      className={`p-3 rounded-xl border transition-all cursor-pointer ${activeId === b.storyboard_id ? "border-[#F2C94C] bg-[#F2C94C]/10" : "border-[#F2C94C]/10 hover:border-[#F2C94C]/40"}`}
                      onClick={() => openBoard(b.storyboard_id)}
                      data-testid={`storyboard-item-${b.storyboard_id}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="font-body text-sm text-white truncate">{b.title}</div>
                          <div className="font-mono-hy text-[9px] text-white">{(b.scenes || []).length} scenes</div>
                        </div>
                        <button
                          onClick={(e) => { e.stopPropagation(); deleteBoard(b.storyboard_id); }}
                          data-testid={`storyboard-delete-${b.storyboard_id}`}
                          className="w-6 h-6 rounded-full flex items-center justify-center text-white hover:text-[#E27D60] hover:bg-[#E27D60]/10"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </aside>

          {/* RIGHT · Editor */}
          <main className="col-span-12 lg:col-span-9 space-y-4">
            <div className="p-5 rounded-2xl bg-[#120E15] border border-[#F2C94C]/15 flex items-center gap-3 flex-wrap">
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                data-testid="storyboard-title-input"
                placeholder="Storyboard title"
                className="flex-1 min-w-[220px] bg-[#0A0A0A] border-[#F2C94C]/20 text-white font-body"
              />
              <button
                onClick={saveBoard}
                disabled={saving}
                data-testid="storyboard-save-btn"
                className="font-mono-hy text-[10px] px-5 py-2.5 rounded-full bg-[#F2C94C] text-[#0A0A0A] hover:bg-[#F2D06B] disabled:opacity-60 transition-all flex items-center gap-2"
              >
                {saving ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Saving…</> : <><Save className="w-3.5 h-3.5" /> {activeId ? "Update" : "Save"}</>}
              </button>
              <button
                onClick={generateReel}
                disabled={reelBuilding || !activeId}
                data-testid="storyboard-reel-btn"
                title={!activeId ? "Save the storyboard first" : "Build a shareable narrated MP4"}
                className="font-mono-hy text-[10px] px-5 py-2.5 rounded-full bg-gradient-to-r from-[#F2C94C] via-[#FF6B4A] to-[#00E5FF] text-[#0A0A0A] hover:brightness-110 disabled:opacity-60 disabled:cursor-not-allowed transition-all flex items-center gap-2"
              >
                {reelBuilding ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Queuing…</> : <><Film className="w-3.5 h-3.5" /> Build Reel · 6 cr</>}
              </button>
            </div>
            {/* Narration controls */}
            <div className="flex items-center gap-3 flex-wrap pt-3 mt-3 border-t border-[#F2C94C]/10">
              <label className="flex items-center gap-2 font-mono-hy text-[10px] text-white cursor-pointer">
                {narrationOn ? <Volume2 className="w-3.5 h-3.5 text-[#00E5FF]" /> : <VolumeX className="w-3.5 h-3.5 text-white" />}
                Narration
                <Switch data-testid="narration-toggle" checked={narrationOn} onCheckedChange={setNarrationOn} />
              </label>
              {narrationOn && voices.length > 0 && (
                <Select value={voice} onValueChange={setVoice}>
                  <SelectTrigger data-testid="narration-voice-select" className="w-44 bg-[#0A0A0A] border-[#00E5FF]/25 h-9 font-body text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#120E15] border-[#00E5FF]/25">
                    {voices.map((v) => (
                      <SelectItem key={v.id} value={v.id} className="font-body text-xs">
                        {v.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <div className="font-mono-hy text-[9px] text-white">
                {narrationOn ? "Each scene's description gets read aloud" : "Silent slideshow with captions only"}
              </div>
            </div>

            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragOver={handleDragOver}
              onDragEnd={handleDragEnd}
              onDragCancel={handleDragCancel}
            >
              <SortableContext items={scenes.map((s) => s._uid)} strategy={rectSortingStrategy}>
                <div className="space-y-0" data-testid="storyboard-scenes">
                  {scenes.map((s, i) => (
                    <div key={s._uid}>
                      <SceneNode
                        scene={s}
                        index={i}
                        totalScenes={scenes.length}
                        reelBuilding={reelBuilding}
                        boardHasReel={boardHasReel}
                        dndId={s._uid}
                        isDropTarget={dragOverSceneId === s._uid}
                        onTitleChange={(idx, val) => updateScene(idx, { title: val })}
                        onDescriptionChange={(idx, val) => updateScene(idx, { description: val })}
                        onPickCharacter={openGalleryPicker}
                        onClearCharacter={clearCharacter}
                        onUploadImage={(idx, file) => uploadSceneImage(idx, file, "frame")}
                        onClearUploaded={clearUploaded}
                        onAddOmni={(idx, file) => uploadSceneImage(idx, file, "omni")}
                        onRemoveOmni={removeOmni}
                        onDropRef={dropRefOnScene}
                        onMoveUp={(idx) => moveScene(idx, -1)}
                        onMoveDown={(idx) => moveScene(idx, 1)}
                        onRemove={removeScene}
                        onSendToPipeline={(scene) => navigate("/studio", {
                          state: {
                            pipelineRoute: {
                              generation_id: scene.character_generation_id,
                              image_url: scene.uploaded_image_url || scene.character_image_url,
                              prompt: scene.description,
                              type: "image",
                            },
                          },
                        })}
                      />
                      {i < scenes.length - 1 && (
                        <NodeConnector fromIndex={i} active={reelBuilding} />
                      )}
                    </div>
                  ))}
                </div>
              </SortableContext>
            </DndContext>

            <button
              onClick={addScene}
              data-testid="storyboard-add-scene-btn"
              className="w-full font-mono-hy text-xs px-6 py-4 rounded-2xl border-2 border-dashed border-[#F2C94C]/25 text-[#F2C94C] hover:border-[#F2C94C] hover:bg-[#F2C94C]/5 transition-all flex items-center justify-center gap-2"
            >
              <Plus className="w-4 h-4" /> Add scene
            </button>
          </main>
        </div>
      </div>

      {/* Character picker dialog — tabbed (Gallery + Pipeline) */}
      <Dialog open={picking != null} onOpenChange={(o) => { if (!o) setPicking(null); }}>
        <DialogContent className="max-w-4xl bg-[#120E15] border-[#F2C94C]/25 text-white">
          <DialogTitle className="sr-only">Pick a character for scene {picking != null ? picking + 1 : ""}</DialogTitle>
          <div className="p-2">
            <div className="font-mono-hy text-[10px] text-[#F2C94C] mb-3">// Pick a character for scene {picking != null ? picking + 1 : ""}</div>
            <Tabs value={pickerTab} onValueChange={setPickerTab} className="w-full">
              <TabsList className="bg-[#0A0A0A] border border-[#F2C94C]/20">
                <TabsTrigger value="gallery" data-testid="picker-tab-gallery" className="font-mono-hy text-[10px] data-[state=active]:bg-[#F2C94C] data-[state=active]:text-[#0A0A0A]">GALLERY</TabsTrigger>
                <TabsTrigger value="pipeline" data-testid="picker-tab-pipeline" className="font-mono-hy text-[10px] data-[state=active]:bg-[#00E5FF] data-[state=active]:text-[#0A0A0A]">PIPELINE</TabsTrigger>
              </TabsList>
              <TabsContent value="gallery">
                {loadingGallery ? (
                  <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#F2C94C]" /></div>
                ) : gallery.length === 0 ? (
                  <div className="text-center py-12">
                    <Sparkles className="w-10 h-10 text-[#F2C94C]/40 mx-auto mb-2" />
                    <div className="font-mono-hy text-[10px] text-white mb-4">You haven't created any images yet.</div>
                    <button
                      onClick={() => navigate("/studio")}
                      className="font-mono-hy text-[10px] px-4 py-2 rounded-full bg-[#F2C94C] text-[#0A0A0A] hover:bg-[#F2D06B]"
                      data-testid="storyboard-goto-studio-btn"
                    >
                      <Wand2 className="w-3.5 h-3.5 inline mr-1.5" /> Create in Studio
                    </button>
                  </div>
                ) : (
                  <div className="grid grid-cols-3 md:grid-cols-4 gap-3 max-h-[60vh] overflow-y-auto mt-3" data-testid="character-picker-grid">
                    {gallery.map((g) => (
                      <button
                        key={g.generation_id}
                        onClick={() => pickCharacter(g)}
                        data-testid={`character-pick-${g.generation_id}`}
                        className="group aspect-square rounded-xl overflow-hidden border border-[#F2C94C]/15 hover:border-[#F2C94C] transition-all"
                      >
                        <img src={g.image_url} alt={g.prompt} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      </button>
                    ))}
                  </div>
                )}
              </TabsContent>
              <TabsContent value="pipeline">
                {pipelineItems.length === 0 ? (
                  <div className="text-center py-12">
                    <LayoutGrid className="w-10 h-10 text-[#00E5FF]/40 mx-auto mb-2" />
                    <div className="font-mono-hy text-[10px] text-white mb-4">No recent pipeline items yet. Create or edit an image in Studio → Image Pipeline.</div>
                    <button
                      onClick={() => navigate("/studio")}
                      className="font-mono-hy text-[10px] px-4 py-2 rounded-full bg-[#00E5FF] text-[#0A0A0A] hover:brightness-110"
                      data-testid="picker-goto-pipeline-btn"
                    >
                      <Wand2 className="w-3.5 h-3.5 inline mr-1.5" /> Open Pipeline
                    </button>
                  </div>
                ) : (
                  <div className="grid grid-cols-3 md:grid-cols-4 gap-3 max-h-[60vh] overflow-y-auto mt-3" data-testid="pipeline-picker-grid">
                    {pipelineItems.map((p, idx) => (
                      <button
                        key={`${p.id || idx}-${p.image_url}`}
                        onClick={() => pickCharacter(p)}
                        data-testid={`pipeline-pick-${p.id || idx}`}
                        className="group aspect-square rounded-xl overflow-hidden border border-[#00E5FF]/20 hover:border-[#00E5FF] transition-all relative"
                      >
                        <img src={p.image_url} alt={p.prompt || "pipeline"} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                        <div className="absolute bottom-1 left-1 font-mono-hy text-[8px] px-1.5 py-0.5 rounded-full bg-[#0A0A0A]/80 border border-[#00E5FF]/40 text-[#00E5FF] tracking-widest">PIPELINE</div>
                      </button>
                    ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </div>
        </DialogContent>
      </Dialog>
      <Footer />
      {/* Session subject lock — same one that carries across Studio */}
      <StoryboardSubjectLock />
      {/* Universal composer — routes to /studio with a preset prompt + mode */}
      <StoryboardDock />
    </div>
  );
}

function StoryboardSubjectLock() {
  const { lock, clearLock } = useSubjectLock();
  return <SubjectLockPill lock={lock} onClear={clearLock} />;
}

function StoryboardDock() {
  const [prompt, setPrompt] = useState("");
  const [dockMode, setDockMode] = useState("image");
  const [autoOn, setAutoOn] = useState(true);
  const navigate = useNavigate();
  return (
    <ChatPromptDock
      mode={dockMode}
      setMode={setDockMode}
      prompt={prompt}
      setPrompt={setPrompt}
      auto={autoOn}
      setAuto={setAutoOn}
      placeholderByMode={{
        image: "Type a scene — I'll open Studio and queue it as an image…",
        video: "Type a scene — I'll open Studio and queue it as a video…",
      }}
      onSubmit={({ prompt: p, mode: m }) => {
        try { sessionStorage.setItem("geneo:dockHandoff", JSON.stringify({ prompt: p, mode: m })); } catch {}
        navigate("/studio");
      }}
    />
  );
}
